import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

// ──────────────────────────────────────────────────────────────
//  COLOURS — plum noir + saturated gold, matches design-spec.
//  Inlined here per Navigation's local convention (so the bar
//  can be edited without touching design-tokens). Mirrors Section
//  2 + Section 6 of /docs/design-spec.md.
// ──────────────────────────────────────────────────────────────
const NAV_BG          = '#2A1820';                    // OBS-nav lifted plum (bar bg)
const NAV_BG_BASE     = '#1D1116';                    // OBS plum noir (drawer panel)
const NAV_BG_DEEPER   = '#15090D';                    // OBS-deep (desktop dropdown panel)
const NAV_BORDER      = 'rgba(201, 169, 97, 0.20)';
const GOLD            = '#C9A961';
const GOLD_LIGHT      = '#E0C685';
const INK_DARK        = '#1A0F08';
const TEXT_DARK_BG    = 'rgba(240, 232, 212, 0.82)';
const TEXT_DARK_DIM   = 'rgba(240, 232, 212, 0.65)';
const EYEBROW_GOLD    = 'rgba(224, 198, 133, 0.88)';
const EYEBROW_DIM     = 'rgba(224, 198, 133, 0.75)';
const OVERLAY_PLUM    = 'rgba(15, 8, 12, 0.65)';

// ──────────────────────────────────────────────────────────────
//  TYPE — single-Cormorant editorial + system-ui for UI
// ──────────────────────────────────────────────────────────────
const FF_SERIF  = "'Cormorant Garamond', Georgia, serif";
const FF_BRAND  = "'Cinzel', 'Cormorant Garamond', Georgia, serif";
const FF_UI     = "system-ui, -apple-system, sans-serif";

const NAV_LINKS = [
  { id: 'landing',    label: 'Home' },
  { id: 'about',      label: 'Our Approach' },
  { id: 'membership', label: 'Membership' },
  { id: 'stories',    label: 'Stories' },
  { id: 'journal',    label: 'Journal' }
];

const PATIENT_LINKS = [
  { id: 'portal',           label: 'My Portal' },
  { id: 'cleo-score',       label: 'My Cleo Score' },
  { id: 'my-results',       label: 'My Results' },
  { id: 'pathology-upload', label: 'Upload Pathology' },
  { id: 'login',            label: 'Sign In' }
];

const ADMIN_LINKS = [
  { id: 'clinical',          label: 'Clinical Dashboard' },
  { id: 'pathology-review',  label: 'Pathology Review' },
  { id: 'retention',         label: 'Patient Outreach' },
  { id: 'admin',             label: 'Admin Panel' }
];

const MOBILE_BREAKPOINT = '(max-width: 1023px)';
const DRAWER_TRANSITION_MS = 280;

// ============================================================
// HOOKS
// ============================================================

// Read viewport width via window.matchMedia. Initial state is read
// synchronously so the first paint doesn't flash desktop-then-mobile.
function useMediaQuery(query) {
  const getMatches = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

// All imperative drawer side effects in one place:
//  A — focus initialFocusRef on open, return to hamburger on close
//  B — Escape to close + Tab focus trap between first and last focusable
//  C — body scroll-lock with scrollbar-width compensation
function useNavDrawer({ open, onClose, hamburgerRef, firstFocusableRef, lastFocusableRef, initialFocusRef }) {
  const wasOpenRef = useRef(false);

  // A: focus management
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      const t = setTimeout(() => {
        if (initialFocusRef.current) initialFocusRef.current.focus();
      }, 60);
      return () => clearTimeout(t);
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      if (hamburgerRef.current) hamburgerRef.current.focus();
    }
    return undefined;
  }, [open, hamburgerRef, initialFocusRef]);

  // B: keyboard handlers
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const first = firstFocusableRef.current;
        const last = lastFocusableRef.current;
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose, firstFocusableRef, lastFocusableRef]);

  // C: body scroll-lock
  useEffect(() => {
    if (!open) return undefined;
    const original = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight
    };
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = scrollbarWidth + 'px';
    }
    return () => {
      document.body.style.overflow = original.overflow;
      document.body.style.paddingRight = original.paddingRight;
    };
  }, [open]);
}

// ============================================================
// SHARED STYLES
// ============================================================

function desktopNavLinkStyle(isActive) {
  return {
    background: isActive ? 'rgba(201, 169, 97, 0.08)' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: FF_SERIF,
    fontSize: 19,
    fontWeight: 500,
    fontStyle: isActive ? 'italic' : 'normal',
    letterSpacing: '0.01em',
    color: isActive ? GOLD_LIGHT : TEXT_DARK_BG,
    borderBottom: isActive ? '2px solid ' + GOLD : '2px solid transparent',
    paddingTop: 10,
    paddingBottom: 8,
    paddingLeft: 14,
    paddingRight: 14,
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    borderRadius: 2
  };
}

// ============================================================
// HAMBURGER (three gold rules → × morph per decision #2)
// ============================================================

function Hamburger({ open, onClick, buttonRef }) {
  const ruleBase = {
    width: 24,
    height: 1.5,
    background: GOLD,
    transition: 'transform 0.24s ease-out, opacity 0.18s ease-out',
    display: 'block',
    transformOrigin: 'center'
  };
  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      aria-controls="primary-mobile-menu"
      style={{
        width: 44,
        height: 44,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0
      }}
    >
      <span style={{
        ...ruleBase,
        opacity: 0.88,
        transform: open ? 'translateY(7.5px) rotate(45deg)' : 'translateY(0) rotate(0)'
      }} />
      <span style={{
        ...ruleBase,
        opacity: open ? 0 : 0.88,
        transform: open ? 'scaleX(0.4)' : 'scaleX(1)'
      }} />
      <span style={{
        ...ruleBase,
        opacity: 0.88,
        transform: open ? 'translateY(-7.5px) rotate(-45deg)' : 'translateY(0) rotate(0)'
      }} />
    </button>
  );
}

// ============================================================
// MOBILE BAR (< 1024px) — closed-state bar with wordmark + hamburger
// ============================================================

function MobileBar({ onNavigate, drawerOpen, onToggleDrawer, hamburgerRef }) {
  return (
    <nav style={{
      background: NAV_BG,
      borderBottom: '1px solid ' + NAV_BORDER,
      padding: '0 20px',
      display: 'flex',
      alignItems: 'center',
      height: 108,
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <div
        onClick={() => onNavigate('landing')}
        style={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}
      >
        <div style={{
          fontFamily: FF_BRAND,
          fontSize: 'clamp(22px, 5vw, 36px)',
          fontWeight: 500,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: GOLD,
          lineHeight: 1,
          paddingRight: '0.28em',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          Cleo&apos;s Secret
        </div>
        <div style={{
          fontFamily: FF_SERIF,
          fontSize: 'clamp(9px, 2vw, 13px)',
          fontWeight: 500,
          fontStyle: 'italic',
          letterSpacing: '0.18em',
          color: EYEBROW_GOLD,
          marginTop: 6,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          Women&apos;s Health Sanctuary
        </div>
      </div>

      <Hamburger
        open={drawerOpen}
        onClick={onToggleDrawer}
        buttonRef={hamburgerRef}
      />
    </nav>
  );
}

// ============================================================
// DRAWER PRIMITIVES
// ============================================================

function DrawerLink({ link, isActive, onSelect, linkRef }) {
  const [hover, setHover] = useState(false);
  const highlighted = isActive || hover;
  return (
    <button
      ref={linkRef || null}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: isActive
          ? 'rgba(201, 169, 97, 0.08)'
          : hover ? 'rgba(201, 169, 97, 0.04)' : 'transparent',
        border: 'none',
        borderLeft: isActive ? '2px solid ' + GOLD : '2px solid transparent',
        cursor: 'pointer',
        fontFamily: FF_SERIF,
        fontSize: 19,
        fontWeight: 500,
        fontStyle: highlighted ? 'italic' : 'normal',
        letterSpacing: '0.01em',
        color: highlighted ? GOLD_LIGHT : TEXT_DARK_BG,
        padding: '16px 22px',
        transition: 'all 0.2s ease',
        minHeight: 44
      }}
    >{link.label}</button>
  );
}

function DrawerEyebrow({ children }) {
  return (
    <div style={{
      fontFamily: FF_SERIF,
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: EYEBROW_DIM,
      padding: '28px 22px 10px',
      borderTop: '1px solid rgba(201, 169, 97, 0.12)',
      marginTop: 16
    }}>{children}</div>
  );
}

// ============================================================
// MOBILE DRAWER (portal'd to document.body)
// ============================================================

function MobileDrawer({ open, onClose, onNavigate, currentPage, isAdmin, lastFocusableLinkId, firstFocusableRef, lastFocusableRef, initialFocusRef }) {
  if (typeof document === 'undefined') return null;

  const handleSelect = (id) => () => {
    onNavigate(id);
    onClose();
  };

  // React 18 doesn't natively understand `inert`; we toggle the attribute
  // via conditional spread. Empty-string value is a valid HTML boolean
  // attribute; the browser treats the attribute's presence as truthy.
  const inertProps = !open ? { inert: '' } : {};

  return ReactDOM.createPortal(
    <div
      id="primary-mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      aria-hidden={!open}
      {...inertProps}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        pointerEvents: open ? 'auto' : 'none'
      }}
    >
      {/* Overlay — click to close */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: OVERLAY_PLUM,
          opacity: open ? 1 : 0,
          transition: `opacity ${DRAWER_TRANSITION_MS}ms ease-out`
        }}
      />

      {/* Panel — slides in from right */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(400px, 88vw)',
          background: NAV_BG_BASE,
          borderLeft: '1px solid ' + NAV_BORDER,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform ${DRAWER_TRANSITION_MS}ms ease-out`,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: open ? '-12px 0 32px rgba(0, 0, 0, 0.45)' : 'none'
        }}
      >
        {/* Header — mirrors bar height with mini wordmark + close × */}
        <div style={{
          height: 108,
          padding: '0 20px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid ' + NAV_BORDER,
          flexShrink: 0
        }}>
          <div
            onClick={() => { onNavigate('landing'); onClose(); }}
            style={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}
          >
            <div style={{
              fontFamily: FF_BRAND,
              fontSize: 'clamp(20px, 4.5vw, 28px)',
              fontWeight: 500,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: GOLD,
              lineHeight: 1,
              paddingRight: '0.28em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              Cleo&apos;s Secret
            </div>
            <div style={{
              fontFamily: FF_SERIF,
              fontSize: 'clamp(9px, 1.8vw, 12px)',
              fontWeight: 500,
              fontStyle: 'italic',
              letterSpacing: '0.18em',
              color: EYEBROW_GOLD,
              marginTop: 6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              Women&apos;s Health Sanctuary
            </div>
          </div>

          {/* Close × — separate element, first focusable in DOM order (focus-trap left edge) */}
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            aria-label="Close menu"
            style={{
              width: 44,
              height: 44,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              position: 'relative',
              flexShrink: 0
            }}
          >
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 22,
              height: 1.5,
              background: GOLD,
              opacity: 0.88,
              transform: 'translate(-50%, -50%) rotate(45deg)'
            }} />
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 22,
              height: 1.5,
              background: GOLD,
              opacity: 0.88,
              transform: 'translate(-50%, -50%) rotate(-45deg)'
            }} />
          </button>
        </div>

        {/* Scrollable content area */}
        <div style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          padding: '20px 0 40px'
        }}>
          {/* CTA stack: Begin your journey (initial-focus target) + Clinical */}
          <div style={{ padding: '0 22px 8px' }}>
            <button
              ref={initialFocusRef}
              onClick={handleSelect('intake')}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_LIGHT; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
              style={{
                fontFamily: FF_UI,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.20em',
                textTransform: 'uppercase',
                padding: '16px 28px',
                background: GOLD,
                color: INK_DARK,
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                minHeight: 48,
                width: '100%',
                marginBottom: 10,
                transition: 'background 0.2s'
              }}
            >Begin your journey</button>

            <button
              onClick={handleSelect('clinical')}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = GOLD_LIGHT;
                e.currentTarget.style.color = GOLD_LIGHT;
                e.currentTarget.style.background = 'rgba(201, 169, 97, 0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = EYEBROW_GOLD;
                e.currentTarget.style.color = EYEBROW_GOLD;
                e.currentTarget.style.background = 'transparent';
              }}
              style={{
                fontFamily: FF_UI,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.20em',
                textTransform: 'uppercase',
                padding: '14px 28px',
                background: 'transparent',
                color: EYEBROW_GOLD,
                border: '1px solid ' + EYEBROW_GOLD,
                borderRadius: 3,
                cursor: 'pointer',
                minHeight: 48,
                width: '100%',
                transition: 'all 0.2s'
              }}
            >Clinical</button>
          </div>

          {/* Top section — 5 flat top-level links (no eyebrow) */}
          <div style={{ paddingTop: 18 }}>
            {NAV_LINKS.map((link) => (
              <DrawerLink
                key={link.id}
                link={link}
                isActive={currentPage === link.id}
                onSelect={handleSelect(link.id)}
              />
            ))}
          </div>

          {/* MY ACCOUNT */}
          <DrawerEyebrow>My Account</DrawerEyebrow>
          {PATIENT_LINKS.map((link) => (
            <DrawerLink
              key={link.id}
              link={link}
              isActive={currentPage === link.id}
              onSelect={handleSelect(link.id)}
              linkRef={link.id === lastFocusableLinkId ? lastFocusableRef : null}
            />
          ))}

          {/* CLINICAL — gated by isAdmin. Strict patients.is_admin check;
              see src/components/RequireAdmin.jsx for the matching frontend
              gate on the destination pages. Hidden entirely when !isAdmin so
              non-staff users don't see destinations they'd be denied at. */}
          {isAdmin && (
            <>
              <DrawerEyebrow>Clinical</DrawerEyebrow>
              {ADMIN_LINKS.map((link) => (
                <DrawerLink
                  key={link.id}
                  link={link}
                  isActive={currentPage === link.id}
                  onSelect={handleSelect(link.id)}
                  linkRef={link.id === lastFocusableLinkId ? lastFocusableRef : null}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============================================================
// DESKTOP DROPDOWN PANEL
// ============================================================

function DropdownPanel({ items, currentPage, onNavigate, onClose }) {
  return (
    <div style={{
      position: 'absolute', top: '100%', right: 0, marginTop: 8,
      background: NAV_BG_DEEPER,
      border: '1px solid rgba(224, 198, 133, 0.32)',
      borderRadius: 6, padding: '10px 0', minWidth: 220, zIndex: 200,
      boxShadow: '0 12px 32px rgba(0,0,0,0.45)'
    }}>
      {items.map((link) => {
        const isActive = currentPage === link.id;
        return (
          <button
            key={link.id}
            onClick={() => { onNavigate(link.id); onClose(); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: isActive ? 'rgba(201, 169, 97, 0.10)' : 'transparent',
              border: 'none', cursor: 'pointer',
              fontFamily: FF_SERIF,
              fontSize: 16,
              fontWeight: 400,
              fontStyle: isActive ? 'italic' : 'normal',
              letterSpacing: '0.005em',
              color: isActive ? GOLD : TEXT_DARK_DIM,
              padding: '11px 22px',
              transition: 'background 0.15s, color 0.15s'
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'rgba(201, 169, 97, 0.06)';
                e.currentTarget.style.color = 'rgba(240, 232, 212, 0.92)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = TEXT_DARK_DIM;
              }
            }}
          >{link.label}</button>
        );
      })}
    </div>
  );
}

// ============================================================
// DESKTOP BAR (≥ 1024px) — preserved canonical from Session 1
// ============================================================

function DesktopBar({ onNavigate, currentPage, isAdmin }) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [patientOpen, setPatientOpen] = useState(false);

  const closeAll = () => {
    setAdminOpen(false);
    setPatientOpen(false);
  };

  return (
    <>
      <nav style={{
        background: NAV_BG,
        borderBottom: '1px solid ' + NAV_BORDER,
        padding: '0 48px',
        display: 'flex',
        alignItems: 'center',
        height: 108,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        {/* Brand wordmark */}
        <div
          onClick={() => onNavigate('landing')}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <div style={{
            fontFamily: FF_BRAND,
            fontSize: 36,
            fontWeight: 500,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: GOLD,
            lineHeight: 1,
            paddingRight: '0.28em'
          }}>
            Cleo&apos;s Secret
          </div>
          <div style={{
            fontFamily: FF_SERIF,
            fontSize: 13,
            fontWeight: 500,
            fontStyle: 'italic',
            letterSpacing: '0.20em',
            color: EYEBROW_GOLD,
            marginTop: 8
          }}>
            Women&apos;s Health Sanctuary
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Nav links */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {NAV_LINKS.map((link) => {
            const isActive = currentPage === link.id;
            return (
              <button
                key={link.id}
                onClick={() => onNavigate(link.id)}
                style={desktopNavLinkStyle(isActive)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = GOLD_LIGHT;
                    e.currentTarget.style.fontStyle = 'italic';
                    e.currentTarget.style.background = 'rgba(201, 169, 97, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.color = TEXT_DARK_BG;
                    e.currentTarget.style.fontStyle = 'normal';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >{link.label}</button>
            );
          })}

          {/* My Account dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setPatientOpen(!patientOpen); setAdminOpen(false); }}
              style={{
                ...desktopNavLinkStyle(
                  currentPage === 'portal' || currentPage === 'cleo-score' ||
                  currentPage === 'my-results' || currentPage === 'pathology-upload' ||
                  currentPage === 'login'
                ),
                display: 'flex',
                alignItems: 'center',
                gap: 7
              }}
            >
              My Account
              <span style={{ fontSize: 8, opacity: 0.7, transform: 'translateY(-1px)' }}>
                {patientOpen ? '▲' : '▼'}
              </span>
            </button>
            {patientOpen && (
              <DropdownPanel
                items={PATIENT_LINKS}
                currentPage={currentPage}
                onNavigate={onNavigate}
                onClose={() => setPatientOpen(false)}
              />
            )}
          </div>
        </div>

        {/* Diamond accent between nav links and CTA */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          margin: '0 24px', opacity: 0.55
        }} aria-hidden="true">
          <div style={{ width: 16, height: '0.5px', background: GOLD }} />
          <div style={{ width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)' }} />
          <div style={{ width: 16, height: '0.5px', background: GOLD }} />
        </div>

        {/* Primary CTA */}
        <button
          onClick={() => onNavigate('intake')}
          onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_LIGHT; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
          style={{
            fontFamily: FF_UI,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.20em',
            textTransform: 'uppercase',
            padding: '11px 28px',
            background: GOLD,
            color: INK_DARK,
            border: 'none',
            borderRadius: 3,
            cursor: 'pointer',
            transition: 'background 0.25s',
            whiteSpace: 'nowrap'
          }}
        >Begin your journey</button>

        {/* Clinical chip — gated by isAdmin. Strict patients.is_admin check;
            see src/components/RequireAdmin.jsx for the matching frontend
            gate on the destination pages. */}
        {isAdmin && (
          <div style={{ position: 'relative', marginLeft: 14 }}>
            <button
              onClick={() => { setAdminOpen(!adminOpen); setPatientOpen(false); }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = GOLD_LIGHT;
                e.currentTarget.style.color = GOLD_LIGHT;
                e.currentTarget.style.background = 'rgba(201, 169, 97, 0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = EYEBROW_GOLD;
                e.currentTarget.style.color = EYEBROW_GOLD;
                e.currentTarget.style.background = 'transparent';
              }}
              style={{
                background: 'transparent',
                border: '1px solid ' + EYEBROW_GOLD,
                borderRadius: 3,
                cursor: 'pointer',
                fontFamily: FF_UI,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.20em',
                textTransform: 'uppercase',
                color: EYEBROW_GOLD,
                padding: '11px 28px',
                transition: 'all 0.25s',
                whiteSpace: 'nowrap'
              }}
            >Clinical</button>
            {adminOpen && (
              <DropdownPanel
                items={ADMIN_LINKS}
                currentPage={currentPage}
                onNavigate={onNavigate}
                onClose={() => setAdminOpen(false)}
              />
            )}
          </div>
        )}
      </nav>

      {/* Click-elsewhere to close dropdowns */}
      {(adminOpen || patientOpen) && (
        <div
          onClick={closeAll}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 99, background: 'transparent'
          }}
        />
      )}
    </>
  );
}

// ============================================================
// TOP-LEVEL NAVIGATION
// ============================================================

export default function Navigation({ onNavigate, currentPage, isAdmin }) {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const hamburgerRef = useRef(null);
  const firstFocusableRef = useRef(null);   // close × in drawer header
  const lastFocusableRef = useRef(null);    // last drawer link (admin if shown, else last patient link)
  const initialFocusRef = useRef(null);     // "Begin your journey" CTA

  // The drawer's DOM-last focusable item, used to close the Tab focus-trap
  // loop. When isAdmin, ADMIN_LINKS renders after PATIENT_LINKS so the trap
  // anchors on the last admin link. When !isAdmin, ADMIN_LINKS is hidden and
  // PATIENT_LINKS becomes DOM-last (its final item is the 'login' / Sign In
  // link). Both DrawerLink renders use the same id-comparison predicate — the
  // ref naturally attaches to whichever section's last link currently sits
  // last in the DOM, with no JSX branching at the attachment site.
  const lastFocusableLinkId = useMemo(() => {
    if (isAdmin) return ADMIN_LINKS[ADMIN_LINKS.length - 1].id;
    return PATIENT_LINKS[PATIENT_LINKS.length - 1].id;
  }, [isAdmin]);

  // Reset drawer when transitioning to desktop, so stale-open state doesn't survive a resize
  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((o) => !o), []);

  useNavDrawer({
    open: drawerOpen,
    onClose: closeDrawer,
    hamburgerRef,
    firstFocusableRef,
    lastFocusableRef,
    initialFocusRef
  });

  return (
    <div>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&display=swap"
        rel="stylesheet"
      />
      {isMobile ? (
        <>
          <MobileBar
            onNavigate={onNavigate}
            drawerOpen={drawerOpen}
            onToggleDrawer={toggleDrawer}
            hamburgerRef={hamburgerRef}
          />
          <MobileDrawer
            open={drawerOpen}
            onClose={closeDrawer}
            onNavigate={onNavigate}
            currentPage={currentPage}
            isAdmin={isAdmin}
            lastFocusableLinkId={lastFocusableLinkId}
            firstFocusableRef={firstFocusableRef}
            lastFocusableRef={lastFocusableRef}
            initialFocusRef={initialFocusRef}
          />
        </>
      ) : (
        <DesktopBar onNavigate={onNavigate} currentPage={currentPage} isAdmin={isAdmin} />
      )}
    </div>
  );
}
